import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { filter, map, Observable, of, take } from 'rxjs';
import { BasicDataResult } from 'src/app/core/services/data-orchestrator';
import { EVENTS_FACADE, REGISTER_FACADE } from 'src/app/core/services/facades';
import { AuthRouteSegments } from 'src/app/shared/constants';
import { RegisterSlugStatus } from 'src/app/shared/enums';
import { AccountActivatedBySlugId, AccountActivatedBySlugIdResult } from 'src/app/shared/models/events/events';
import { RegisterAdapter, RegisterAdapterRegisterResult } from 'src/app/shared/models/register-adapter.model';

@Injectable()
export class SlugIdRegisterAdapterService implements RegisterAdapter {
  private readonly registerFacade = inject(REGISTER_FACADE);
  private readonly eventsFacade = inject(EVENTS_FACADE);

  public register(
    password: string,
    route: ActivatedRouteSnapshot,
  ): Observable<BasicDataResult<RegisterAdapterRegisterResult>> {
    const eventData: AccountActivatedBySlugId = {
      slugId: route.paramMap.get(AuthRouteSegments.slugId),
      newPassword: password,
    };

    return this.eventsFacade
      .sendEventWithResponse({
        eventType: 'AccountActivatedBySlugId',
        jsonData: eventData,
      })
      .pipe(
        map((result: BasicDataResult<AccountActivatedBySlugIdResult>) => {
          if (result?.data && !result.loading && !result.error) {
            return {
              ...result,
              data: {
                email: result.data.AccountDetails.Email,
                password,
              },
            };
          }

          return {
            ...result,
            data: null,
          };
        }),
      );
  }

  public canRegister(route: ActivatedRouteSnapshot): Observable<boolean> {
    const slugId = route.paramMap.get(AuthRouteSegments.slugId);

    if (!slugId) {
      return of(false);
    }

    const { data$ } = this.registerFacade.getRegisterSlugStatus({
      slugId,
      readFromCacheFirst: false,
    });

    return data$.pipe(
      filter((result) => !result.loading),
      map((result) => {
        if (result.error) {
          return false;
        }

        return result.data.Status === RegisterSlugStatus.Valid;
      }),
      take(1),
    );
  }
}
